import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowLeft, Shield } from "lucide-react";
import { Helmet } from "react-helmet-async";
import { useEffect } from "react";

const fadeUp = {
  hidden: { opacity: 0, y: 20 },
  visible: { opacity: 1, y: 0, transition: { duration: 0.5 } }
};

const PrivacyPolicy = () => {
  // Scroll to top on page load
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-[#F0F2F5]">
      <Helmet>
        <title>Privacy Policy | Modern Path AI</title>
        <meta name="description" content="Privacy Policy for Modern Path AI. Learn how we collect, use, and protect your personal information." />
        <link rel="canonical" href="https://modernpathai.com/privacy" />
      </Helmet>

      {/* Navigation */}
      <nav className="fixed top-0 left-0 right-0 z-50 bg-[#0A0A0A]/90 backdrop-blur-md border-b border-white/5">
        <div className="max-w-[1400px] mx-auto px-4 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2 text-[#A0A0A0] hover:text-[#00FFFF] transition-colors">
            <ArrowLeft className="w-4 h-4" />
            <span className="text-sm font-medium">Back to Home</span>
          </Link>
          <div className="flex items-center gap-2 text-[#00FFFF]">
            <Shield className="w-4 h-4" />
            <span className="text-sm font-medium">Privacy Policy</span>
          </div>
        </div>
      </nav>

      {/* Content */}
      <main className="pt-24 pb-20 px-4">
        <div className="max-w-[800px] mx-auto">
          <motion.div
            initial="hidden"
            animate="visible"
            variants={fadeUp}
          >
            {/* Header */}
            <div className="text-center mb-12">
              <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-4" style={{ fontFamily: 'Syne, sans-serif' }}>
                Privacy Policy for Modern Path AI
              </h1>
              <p className="text-[#A0A0A0]">Effective Date: March 19, 2026</p>
            </div>

            {/* Policy Content */}
            <div className="prose prose-invert max-w-none space-y-8">
              <p className="text-[#A0A0A0] text-lg leading-relaxed">
                At Modern Path AI, we respect your privacy and are committed to protecting the personal data of our clients and website visitors. This Privacy Policy outlines how we collect, use, and safeguard your information when you interact with our website or our lead generation advertisements on Meta (Facebook and Instagram).
              </p>

              {/* Section 1 */}
              <section className="space-y-4">
                <h2 className="text-xl md:text-2xl font-bold text-[#00FFFF]" style={{ fontFamily: 'Syne, sans-serif' }}>
                  1. Information We Collect
                </h2>
                <p className="text-[#A0A0A0] leading-relaxed">
                  We collect information that you voluntarily provide to us through our lead forms, contact forms, and demo requests, including:
                </p>
                <ul className="list-disc list-inside text-[#A0A0A0] space-y-2 ml-4">
                  <li>Name and Job Title</li>
                  <li>Contact Information (Email address, Phone number)</li>
                  <li>Business Details (Company name, industry, number of service vehicles)</li>
                  <li>Communication Preferences</li>
                </ul>
              </section>

              {/* Section 2 */}
              <section className="space-y-4">
                <h2 className="text-xl md:text-2xl font-bold text-[#00FFFF]" style={{ fontFamily: 'Syne, sans-serif' }}>
                  2. How We Use Your Information
                </h2>
                <p className="text-[#A0A0A0] leading-relaxed">
                  We use the information collected to:
                </p>
                <ul className="list-disc list-inside text-[#A0A0A0] space-y-2 ml-4">
                  <li>Provide, operate, and maintain our AI Voice Agent services.</li>
                  <li>Process your requests for demos or consultations.</li>
                  <li>Communicate with you regarding your inquiries (via phone, email, or SMS).</li>
                  <li>Improve our marketing and advertising efforts.</li>
                </ul>
              </section>

              {/* Section 3 */}
              <section className="space-y-4">
                <h2 className="text-xl md:text-2xl font-bold text-[#00FFFF]" style={{ fontFamily: 'Syne, sans-serif' }}>
                  3. Data Sharing and Disclosure
                </h2>
                <p className="text-[#A0A0A0] leading-relaxed">
                  Modern Path AI does not sell, rent, or lease your personal information to third parties. We may share data with trusted service providers (such as CRM systems or email marketing tools) solely to facilitate our services to you.
                </p>
              </section>

              {/* Section 4 */}
              <section className="space-y-4">
                <h2 className="text-xl md:text-2xl font-bold text-[#00FFFF]" style={{ fontFamily: 'Syne, sans-serif' }}>
                  4. Data Security
                </h2>
                <p className="text-[#A0A0A0] leading-relaxed">
                  We implement industry-standard security measures to maintain the safety of your personal information. However, no method of transmission over the Internet is 100% secure.
                </p>
              </section>

              {/* Section 5 */}
              <section className="space-y-4">
                <h2 className="text-xl md:text-2xl font-bold text-[#00FFFF]" style={{ fontFamily: 'Syne, sans-serif' }}>
                  5. Your Rights (PIPEDA Compliance)
                </h2>
                <p className="text-[#A0A0A0] leading-relaxed">
                  As a user in Canada, you have the right to:
                </p>
                <ul className="list-disc list-inside text-[#A0A0A0] space-y-2 ml-4">
                  <li>Access the personal information we hold about you.</li>
                  <li>Request the correction of inaccurate data.</li>
                  <li>Request the deletion of your data from our systems.</li>
                </ul>
              </section>

              {/* Section 6 */}
              <section className="space-y-4">
                <h2 className="text-xl md:text-2xl font-bold text-[#00FFFF]" style={{ fontFamily: 'Syne, sans-serif' }}>
                  6. Cookies and Tracking
                </h2>
                <p className="text-[#A0A0A0] leading-relaxed">
                  Our website may use cookies to enhance user experience and analyze traffic. You can choose to set your web browser to refuse cookies, though some parts of the site may not function properly.
                </p>
              </section>

              {/* Section 7 */}
              <section className="space-y-4">
                <h2 className="text-xl md:text-2xl font-bold text-[#00FFFF]" style={{ fontFamily: 'Syne, sans-serif' }}>
                  7. Contact Us
                </h2>
                <p className="text-[#A0A0A0] leading-relaxed">
                  If you have any questions about this Privacy Policy, please contact us at:
                </p>
                <div className="bg-white/5 border border-white/10 rounded-xl p-6 mt-4">
                  <p className="font-bold text-white mb-2" style={{ fontFamily: 'Syne, sans-serif' }}>Modern Path AI</p>
                  <p className="text-[#A0A0A0]">
                    Email: <a href="mailto:info@modernpathai.com" className="text-[#00FFFF] hover:underline">info@modernpathai.com</a>
                  </p>
                  <p className="text-[#A0A0A0]">
                    Phone: <a href="tel:+16473707545" className="text-[#00FFFF] hover:underline">647-370-7545</a>
                  </p>
                  <p className="text-[#A0A0A0]">
                    Website: <a href="https://www.modernpathai.com" className="text-[#00FFFF] hover:underline">www.modernpathai.com</a>
                  </p>
                </div>
              </section>
            </div>
          </motion.div>
        </div>
      </main>

      {/* Footer */}
      <footer className="py-8 px-4 border-t border-white/5">
        <div className="max-w-[800px] mx-auto text-center">
          <p className="text-[#606060] text-sm">
            © {new Date().getFullYear()} Modern Path AI. All rights reserved.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default PrivacyPolicy;
